import React, { useMemo, useState, useEffect, useRef } from "react";
import {
  MaterialReactTable,
  MRT_VisibilityState,
  type MRT_ColumnDef,
  type MRT_PaginationState,
  MRT_SortingState,
  MRT_RowSelectionState,
} from "material-react-table";
import {
  Eye16Filled,
  MoreHorizontal24Filled,
  Edit16Filled,
  ArrowDownload20Filled,
  ChevronDownRegular,
  ArrowSwap20Regular,
} from "@fluentui/react-icons";
import defaultTableConfig from "../../shared/tableConfig.json";
import { useNavigate } from "react-router-dom";
import {
  Button,
  makeStyles,
  Tooltip,
  Menu,
  MenuTrigger,
  MenuList,
  MenuItem,
  MenuPopover,
  Label,
  useId,
  shorthands,
  Toaster,
  useToastController,
  ToastTitle,
  Toast,
  ToastBody,
  ToastTrigger,
  Link,
} from "@fluentui/react-components";
import { ClientDataType } from "../../types";
import Search from "../../components/Search";
import DropDownBox from "../../components/DropDownBox";
import ASCButton from "../../components/Button";
import "./style.css";
import {
  checkUserPermission,
  convertStringToArray,
  formatedDate,
} from "../../utils";
import apiServices from "../../service";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { UserPermissionsList } from "../../config";
import ReactQuillComponent from "../../components/ReactQuillComponent";
import { debounce } from "lodash";
import SendMail from "./SendMail";
import { IconButton } from "@mui/material";
import { resetReloadClientList } from "../../redux/formUpdateReducer";
import ExportMain from "../Export/ExportMain";
import MainPage from "../ClientPartnerSplit/MainPage";
import {
  setClientValue,
  setFilterSelectionData,
  setPartnerIDs,
} from "../../redux/clientSlice";
import ASCDropDown from "../../components/DropDown";
import MainTab from "./SavedSearch/MainTab";
import { TableListLoader } from "../../components/Loader/TableListLoader";
import { clearString } from "../../redux/SavedSearch/searchId";
import { clearSearch } from "../../redux/SavedSearch/updateSearchId";
import { clearSelectionType } from "../../redux/SavedSearch/extractTypes";
import {
  fetchUserPreferences,
  savePreferences,
  selectDefaultConfig,
  selectPreferenceStatus,
  selectTableColumnOrder,
  selectTableColumns,
  setTableColumnOrder,
} from "../../redux/userPreferenceSlice";
import { useToastMessage } from "../../components/ToastMessage/ToastMessage";

const useStyles = makeStyles({
  wrapper: {
    columnGap: "15px",
    display: "flex",
    cursor: "pointer",
  },
  contentHeader: {
    marginTop: "0",
  },
  scopeArea: {
    display: "grid",
    gridTemplateRows: "repeat(1fr)",
    justifyItems: "start",
    ...shorthands.gap("2px"),
    maxWidth: "400px",
  },
  popup: {
    height: "auto",
    width: "auto",
    position: "absolute",
    backgroundColor: "white",
    ...shorthands.padding("10px"),
    zIndex: 1000,
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.2)",
    alignItems: "center",
    cursor: "pointer",
  },
});
type ClientDataListProps = {
  [x: string]: any;
  changeMode?: (mode: string) => void;
  setActiveItem?: (item: any) => void;
  search?: string;
  setEncodedCondition?: any;
  encodedCondition?: any;
};
const ClientDataList = (props: ClientDataListProps) => {
  const styles = useStyles();
  const isFirstRender = useRef(true);
  const tableName = "CLIENT_DETAILS";
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const { showToast, toasterId } = useToastMessage();

  // state
  const [hasChanges, setHasChanges] = useState(false);
  const [popupData, setPopupData] = useState<string | null>(null);
  const [popupVisible, setPopupVisible] = useState(false);
  const [popupPosition, setPopupPosition] = useState({ top: 0, left: 0 });
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [isSendMailDialogOpen, setSendMailDialogOpen] = useState(false);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [IsContentloading, setIsContentLoading] = useState(true);
  const [openSplit, setOpenSplit] = useState<boolean>(false);
  const [isOpenDialog, setIsOpenDialog] = useState(false);
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [ClientPartner, setClientPartner] = useState<any>(null);
  const [partnerID, setPartnerID] = useState("");
  const [client, setClient] = useState<ClientDataType[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectClient, setSelectClient] = useState<{
    customer_id: string;
    first_names: string;
    last_name: string;
  }>({ customer_id: "", first_names: "", last_name: "" });
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [sorting, setSorting] = useState<MRT_SortingState>([]);

  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>(
    {
      initials: false,
      former_last_name: false,
    }
  );

  // redux hooks
  const loggedUser = useSelector((state: RootState) => state.authUser);
  const isReloadClientList = useSelector(
    (state: RootState) => state.formUpdated.isReloadClientList
  );
  const filterSelectionData = useSelector(
    (state: RootState) => state.selectionFilter.storedValue
  );

  const columns = useSelector(selectTableColumns(tableName));
  const reduxColumnOrder = useSelector(selectTableColumnOrder(tableName));
  const defaultConfig = useSelector(selectDefaultConfig);
  const preferenceStatus = useSelector(selectPreferenceStatus);
  const userPreferences = useSelector(
    (state: RootState) => state.userPreferences.preferences
  );
  const defaultColumns =
    defaultTableConfig.tables.contextSpecific[tableName]?.columns || [];
  const tables = useSelector(
    (state: RootState) =>
      state.userPreferences.preferences?.tables || {
        defaultConfig: {},
        contextSpecific: {},
      }
  );
  const contextSpecific = useSelector(
    (state: RootState) =>
      state.userPreferences.preferences?.tables?.contextSpecific || {}
  );
  const tableSettings = useSelector(
    (state: RootState) =>
      state.userPreferences.preferences?.tables?.contextSpecific?.[tableName] ||
      {}
  );
  const tableInitialState = useSelector(
    (state: RootState) =>
      state.userPreferences.preferences?.tables?.contextSpecific?.[tableName]
        ?.initialState || {}
  );

  const handleOpen = () => setIsOpenDialog(true);
  const handleClose = () => setIsOpenDialog(false);
  const handleOpenSearches = () => {
    props.setTabOpen(true);
  };
  const handleCloseSplit = () => {
    setOpenSplit(false);
    clearExtractFilters();
  };

  const openSendMailDialog = (
    customer_id: string,
    first_names: string,
    last_name: string
  ) => {
    setSendMailDialogOpen(true);
    setSelectClient({ customer_id, first_names, last_name });
  };

  const closeSendMailDialog = () => {
    setSendMailDialogOpen(false);
    props.setReloadList(true);
  };
  const handleCloseMainTab = () => {
    props.setTabOpen(false);
  };

  const OpenSplitComponent = (customer_id: any) => {
    setOpenSplit(true);
    dispatch(setClientValue(customer_id));
  };
  const customColumns = useMemo<MRT_ColumnDef<any>[]>(() => {
    if (!columns || columns.length === 0) {
      return [];
    }

    return columns.map((col: any) => {
      const enhancedCol: MRT_ColumnDef<any> = { ...col };
      if (col.Cell) {
        switch (col.Cell) {
          case "formatedDate":
            enhancedCol.Cell = ({ row }) => (
              <>{formatedDate(row.original[col.accessorKey])}</>
            );
            break;
          case "ReactQuillComponent":
            enhancedCol.Cell = ({ row }) => (
              <ReactQuillComponent value={row.original[col.accessorKey]} />
            );
            break;
          default:
            break;
        }
      }
      return enhancedCol;
    });
  }, [columns]);

  const config = useMemo(
    () => ({
      ...defaultConfig,
      enablePagination: true,
      enableColumnOrdering: true,
      enableColumnDragging: true,
      initialState: {
        ...defaultConfig.initialState,
        density: "compact" as const,
        columnOrder,
        columnPinning: { right: ["mrt-row-actions"], left: ["mrt-row-select"] },
      },
    }),
    [defaultConfig, columnOrder]
  );

  const handleColumnOrderChange = (
    updater: string[] | ((prev: string[]) => string[])
  ) => {
    const newOrder =
      typeof updater === "function" ? updater(columnOrder) : updater;
    setColumnOrder(newOrder);
    const isChanged =
      JSON.stringify(newOrder) !== JSON.stringify(reduxColumnOrder);
    setHasChanges(isChanged);
    dispatch(setTableColumnOrder({ tableName, columnOrder: newOrder }));
  };
  async function fetchUsersData() {
    let scopeSelected =
      props.selectedScope?.length > 0 ? props.selectedScope.join(",") : "";
    setLoading(true);
    setIsContentLoading(true);
    apiServices.client
      .getClientLists(
        pagination.pageIndex + 1,
        pagination.pageSize,
        props.search,
        scopeSelected,
        props.selectedClientType,
        props.searchId,
        props.encodedCondition
      )
      .then(async (response: any) => {
        if (response) {
          setLoading(false);
          setIsContentLoading(false);
          props.setApplyFilter(false);
          await setClient(
            response.data &&
              response.data.data &&
              response.data.data.customerList
              ? response.data.data.customerList
              : []
          );
          await setRowCount(
            response.data && response.data.data && response.data.data.count
              ? response.data.data.count
              : 0
          );
          dispatch(resetReloadClientList());
        } else {
          setLoading(false);
          setIsContentLoading(false);
          props.setApplyFilter(false);
          dispatch(resetReloadClientList());
          showToast("Something went wrong", "", "error");
        }
      })

      .catch((error) => {
        setLoading(false);
        setIsContentLoading(false);
        if (error.response && ![401, 403].includes(error.response?.status)) {
          showToast("Something went wrong.", "", "error");
        }
      });
  }

  function exportApi(customerId: any) {
    apiServices.exportToExcel
      .get(customerId)
      .then(async (response) => {
        const blob = new Blob([response.data]);
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "ClientData.xlsx");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  const getClientPartner = (id: any) => {
    apiServices.ClientPartnerSplit.getClientDetail(id)
      .then((res) => {
        setPartnerID(res.data.data.NameAndAddress.associated_with);
        dispatch(setPartnerIDs(res.data.data.NameAndAddress.associated_with));

        setClientPartner(res.data.data);
      })
      .catch((error) => {
        console.log(error);
      });
  };

  const clearExtractFilters = async () => {
    setLoading(true);
    props.setSearch([]);
    props.setSelectedScope([]);
    props.setSelectedClientType([]);
    dispatch(setFilterSelectionData([]));
    props.setSelectedSearches([]);
    props.setSelectedSearch([]);
    setPagination({ pageIndex: 0, pageSize: 10 });
    setClient([]);
    setRowCount(0);
    dispatch(clearString());
    dispatch(clearSearch());
    dispatch(clearSelectionType());
    try {
      await fetchUsersData();
    } catch (error) {
      console.error("Error while fetching users:", error);
    } finally {
      setLoading(false);
    }
  };

  const savePreferencesAutomatically = useRef(
    debounce(async () => {
      try {
        const { message } = await dispatch(
          savePreferences(defaultTableConfig)
        ).unwrap();
      } catch (error) {
        console.error("Error saving preferences:", error);
      }
    }, 500)
  ).current;
  useEffect(() => {
    if (filterSelectionData.length > 0) {
      setClient(filterSelectionData);
    }
  }, [filterSelectionData]);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    fetchUsersData();
  }, [
    pagination.pageIndex,
    pagination.pageSize,
    props.applyFilter,
    props.reloadList,
    isReloadClientList,
  ]);
  useEffect(() => {
    const savedOrder =
      reduxColumnOrder.length > 0
        ? reduxColumnOrder
        : columns.map((col: any) => col.accessorKey);

    if (JSON.stringify(savedOrder) !== JSON.stringify(columnOrder)) {
      setColumnOrder(savedOrder);
      setHasChanges(false); // Reset changes when loading saved order
    }
  }, [reduxColumnOrder, columns, columnOrder]);
  useEffect(() => {
    if (props.setIsLoading !== undefined) {
      props.setIsLoading(loading);
    }
  }, [loading]);

  useEffect(() => {
    const debouncedFunction = debounce(() => {
      if (
        props.selectedScope?.length > 0 &&
        props.search &&
        props.search.length >= 2
      ) {
        fetchUsersData();
      } else if (props.search == "") {
        fetchUsersData();
      } else {
      }
    }, 300);
    debouncedFunction();
    return () => {
      debouncedFunction.cancel();
    };
  }, [props.search, props.selectedScope]);
  useEffect(() => {
    const selectedCustomerId = Object.keys(rowSelection)[0];
    if (selectedCustomerId) {
      const selectedCustomerValue = client.find(
        (c) => c.customer_id === selectedCustomerId
      );

      if (selectedCustomerValue && props.setActiveItem) {
        props.setActiveItem(selectedCustomerValue);
      }
    }
  }, [rowSelection, client, props]);

  useEffect(() => {
    setRowSelection({ [props.activeClient.customer_id]: true });
  }, [props.activeClient]);

  useEffect(() => {
    const columnsReady = Array.isArray(columns) && columns.length > 0;
    const defaultReady =
      Array.isArray(defaultColumns) && defaultColumns.length > 0;

    if (
      columnsReady &&
      defaultReady &&
      JSON.stringify(columns) !== JSON.stringify(defaultColumns)
    ) {
      savePreferencesAutomatically();
    }
  }, [columns, defaultColumns]);
  useEffect(() => {
    if (preferenceStatus === "idle") {
      dispatch(fetchUserPreferences());
    }
  }, [preferenceStatus]);
  useEffect(() => {
    setColumnOrder((prevColumnOrder) => {
      let updatedOrder = [...prevColumnOrder];

      if (!updatedOrder.includes("mrt-row-actions")) {
        updatedOrder.push("mrt-row-actions");
      }

      if (!updatedOrder.includes("mrt-row-select")) {
        updatedOrder.push("mrt-row-select");
      }

      return prevColumnOrder === updatedOrder ? prevColumnOrder : updatedOrder;
    });
  }, []);
  const handleSavePreference = async () => {
    setIsProcessing(true);
    const updatedSettings = {
      ...userPreferences,
      tables: {
        ...tables,
        contextSpecific: {
          ...contextSpecific,
          [tableName]: {
            ...tableSettings,
            initialState: {
              ...tableInitialState,
              columnOrder: columnOrder?.length
                ? columnOrder
                : tableInitialState?.columnOrder,
            },
          },
        },
      },
    };

    const isUpdatedSettingsEmpty =
      !updatedSettings ||
      Object.keys(updatedSettings).length === 0 ||
      !updatedSettings.tables ||
      Object.keys(updatedSettings.tables).length === 0;

    const finalSettings = isUpdatedSettingsEmpty
      ? defaultTableConfig
      : updatedSettings;
    // const finalSettings = defaultTableConfig;
    try {
      const { message } = await dispatch(
        savePreferences(finalSettings)
      ).unwrap();
      showToast(message, "", "success");
      setIsProcessing(false);
      setHasChanges(false);
    } catch (error) {
      setIsProcessing(false);
      console.error("Error saving preferences:", error);
    }
  };

  return (
    <>
      <Toaster toasterId={toasterId} position="top-end" timeout={5000} />
      {IsContentloading ? (
        <TableListLoader rows={10} columns={6} />
      ) : (
        <div className="client-list-wrapper">
          {customColumns.length > 0 && (
            <MaterialReactTable
              getRowId={(row) => row.customer_id}
              rowCount={rowCount}
              muiTableHeadCellProps={config.muiTableHeadCellProps}
              muiToolbarAlertBannerProps={{ sx: { display: "none" } }}
              muiTablePaginationProps={{
                rowsPerPageOptions: [10, 50, 100, 200, 500],
              }}
              renderTopToolbarCustomActions={({ table }) => (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <div style={{ display: "flex" }}>
                    <div className="global-search-filter">
                      <Search
                        setSearch={props.setSearch}
                        search={props.search}
                        isLoading={props.isLoading}
                        placeholder="Search by first name or last name"
                        width={"260px"}
                      />
                      <DropDownBox
                        disabled={
                          props.search && props.search !== "" ? false : true
                        }
                        placeholder={
                          props.selectedScope.length > 0
                            ? props.selectedScope
                                .map(
                                  (s: string) =>
                                    s.charAt(0).toUpperCase() + s.slice(1)
                                )
                                .join(", ")
                            : "Scope"
                        }
                        multiselect={true}
                        values={props.scopeData}
                        selectedOptions={props.selectedScope}
                        onOptionSelect={(event: any, option: string | any) => {
                          props.setSelectedScope(option.selectedOptions);
                        }}
                      />
                      <ASCDropDown
                        placeholder={
                          props.selectedClientType.length > 0
                            ? props.selectedClientType
                            : "Client Type"
                        }
                        optionsFetcher={props.fetchClientTypeOptions}
                        clearable
                        onSelectionChange={(selectedValue: any) => {
                          props.setSelectedClientType(selectedValue);
                        }}
                      />

                      {!convertStringToArray(
                        process.env.REACT_APP_HIDE_CTA_BUTTON
                      ).includes("saved_searches") ? (
                        <ASCButton
                          shape="rounded"
                          icon={<ChevronDownRegular />}
                          iconPosition="after"
                          style={{
                            fontWeight: 400,
                            color: "#606060",
                            border: "1px solid grey",
                          }}
                          onClick={handleOpenSearches}
                          label="Saved Searches"
                        />
                      ) : (
                        ""
                      )}

                      <ASCButton
                        shape="rounded"
                        disabled={props.selectedClientType != "" ? false : true}
                        label={"Apply Filter"}
                        className={
                          props.selectedClientType != ""
                            ? "asc-button-primary"
                            : ""
                        }
                        appearance="primary"
                        onClick={() => {
                          props.setApplyFilter(true);
                        }}
                        size="medium"
                      />
                      {(props.searchId.length > 0 ||
                        filterSelectionData.length > 0 ||
                        props.selectedClientType.length > 0 ||
                        props.selectedScope.length > 0) && (
                        <ASCButton
                          shape="rounded"
                          appearance="primary"
                          className="asc-button-primary"
                          size="medium"
                          onClick={clearExtractFilters}
                          label="Clear Filter"
                        />
                      )}
                    </div>{" "}
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    {!convertStringToArray(
                      process.env.REACT_APP_HIDE_CTA_BUTTON
                    ).includes("saved_searches") && (
                      <Tooltip
                        content="Export"
                        relationship="label"
                        appearance="inverted"
                        positioning="below"
                        withArrow
                      >
                        {props.searchId?.length > 0 ||
                        filterSelectionData?.length > 0 ? (
                          <IconButton
                            onClick={handleOpen}
                            disabled={
                              !(
                                props.searchId?.length > 0 ||
                                filterSelectionData?.length > 0
                              )
                            }
                          >
                            <ArrowDownload20Filled />
                          </IconButton>
                        ) : null}
                      </Tooltip>
                    )}
                    {isOpenDialog && (
                      <ExportMain
                        searchIds={props.searchIds}
                        isOpen={isOpenDialog}
                        onClose={handleClose}
                        selectedTab={props.selectedTab}
                        typeOfExtract={props.typeOfExtract}
                        searchId={props.searchId}
                      />
                    )}
                    {!convertStringToArray(
                      process.env.REACT_APP_HIDE_CTA_BUTTON
                    ).includes("client_list_preference") && (
                      <ASCButton
                        shape="rounded"
                        className="asc-button-secondary"
                        appearance="secondary"
                        onClick={handleSavePreference}
                        label="Save Preference"
                        disabled={!hasChanges || isProcessing}
                      />
                    )}
                  </div>
                </div>
              )}
              renderRowActions={({ row, table }) => (
                <div className={styles.wrapper}>
                  {checkUserPermission(
                    loggedUser,
                    UserPermissionsList.ALLOW_VIEW_CLIENT
                  ) && (
                    <Tooltip
                      content="View"
                      relationship="label"
                      positioning="below"
                    >
                      <Button
                        shape="rounded"
                        appearance="subtle"
                        onClick={() => {
                          navigate(
                            `clients/${
                              row.original.associated_type == 1
                                ? row.original.associated_with
                                : row.original.customer_id
                            }/name-and-address/view`
                          );
                        }}
                        icon={<Eye16Filled primaryFill="var(--button-color)" />}
                      />
                    </Tooltip>
                  )}
                  {checkUserPermission(
                    loggedUser,
                    UserPermissionsList.ALLOW_UPDATE_CLIENT
                  ) && (
                    <Tooltip
                      content="Edit"
                      relationship="label"
                      positioning="below"
                    >
                      <Button
                        shape="rounded"
                        appearance="subtle"
                        onClick={() => {
                          navigate(
                            `clients/${
                              row.original.associated_type == 1
                                ? row.original.associated_with
                                : row.original.customer_id
                            }/name-and-address/edit`
                          );
                        }}
                        icon={
                          <Edit16Filled primaryFill="var(--button-color)" />
                        }
                      />
                    </Tooltip>
                  )}
                  {(checkUserPermission(
                    loggedUser,
                    UserPermissionsList.ALLOW_CLIENT_EXPORTS
                  ) ||
                    checkUserPermission(
                      loggedUser,
                      UserPermissionsList.ALLOW_UPDATE_CLIENT
                    )) && (
                    <Menu>
                      <MenuTrigger disableButtonEnhancement>
                        <Button
                          shape="rounded"
                          appearance="subtle"
                          icon={<MoreHorizontal24Filled primaryFill="gray" />}
                        />
                      </MenuTrigger>
                      <MenuPopover>
                        <MenuList>
                          {checkUserPermission(
                            loggedUser,
                            UserPermissionsList.ALLOW_CLIENT_EXPORTS
                          ) && (
                            <MenuItem
                              onClick={() =>
                                exportApi(row.original.customer_id)
                              }
                            >
                              Export to Excel
                            </MenuItem>
                          )}
                          <MenuItem
                            disabled={!row.original.email}
                            onClick={() =>
                              openSendMailDialog(
                                row.original.customer_id,
                                row.original.first_names,
                                row.original.last_name
                              )
                            }
                          >
                            Send Mail
                          </MenuItem>
                          {!convertStringToArray(
                            process.env.REACT_APP_HIDE_CTA_BUTTON
                          ).includes("client_partner_split") ? (
                            <MenuItem
                              onClick={() => {
                                OpenSplitComponent(row.original.customer_id);
                                getClientPartner(row.original.customer_id);
                              }}
                              disabled={row.original.associated_with === null}
                            >
                              Split
                            </MenuItem>
                          ) : (
                            ""
                          )}
                        </MenuList>
                      </MenuPopover>
                    </Menu>
                  )}
                </div>
              )}
              columns={customColumns}
              data={client}
              enableRowSelection={config.enableRowSelection}
              enableColumnActions={config.enableColumnActions}
              enableColumnOrdering={config.enableColumnOrdering}
              enableColumnDragging={config.enableColumnDragging}
              onColumnOrderChange={handleColumnOrderChange}
              enableSorting={config.enableSorting}
              manualSorting={config.manualSorting}
              enablePagination={config.enablePagination}
              enableMultiRowSelection={config.enableMultiRowSelection}
              enableColumnFilters={config.enableColumnFilters}
              enableGlobalFilter={config.enableGlobalFilter}
              enableDensityToggle={config.enableDensityToggle}
              manualPagination={config.manualPagination}
              manualFiltering={config.manualFiltering}
              initialState={config.initialState}
              muiTableProps={config.muiTableProps}
              muiTableBodyRowProps={({ row }) => ({
                ...config.muiTableBodyRowProps,
                onClick: row.getToggleSelectedHandler(),
              })}
              icons={{
                DragHandleIcon: () => (
                  <ArrowSwap20Regular
                    style={{ color: "000", cursor: "grab" }}
                  />
                ),
              }}
              onPaginationChange={setPagination}
              onRowSelectionChange={setRowSelection}
              onSortingChange={setSorting}
              onColumnVisibilityChange={setColumnVisibility}
              state={{ rowSelection, pagination, columnOrder, sorting }}
            />
          )}

          {popupVisible && (
            <div
              className={styles.popup}
              style={{ top: popupPosition.top, left: popupPosition.left }}
            >
              <Label weight="semibold">Headline Note</Label>
              <Tooltip
                withArrow
                content="Example tooltip with an arrow"
                relationship="label"
              >
                <ReactQuillComponent value={popupData ?? ""} />
              </Tooltip>
            </div>
          )}
        </div>
      )}

      {isSendMailDialogOpen && (
        <SendMail
          openSendMailDialog={openSendMailDialog}
          isSendMailDialogOpen={isSendMailDialogOpen}
          closeSendMailDialog={closeSendMailDialog}
          selectClient={selectClient}
          reloadList={props.setReloadList}
          notify={showToast}
        />
      )}
      {props.tabOpen && (
        <MainTab
          extract={props.extract}
          setIsDialogOpens={props.setIsDialogOpens}
          handleAddDialogBox={props.handleAddDialogBox}
          setSelectedItem={props.setSelectedItem}
          selectedItem={props.selectedItem}
          setSelectedItemTwo={props.setSelectedItemTwo}
          hasTableTwo={props.hasTableTwo}
          handleAddSearchClick={props.handleAddSearchClick}
          selectedMenuItem={props.selectedMenuItem}
          renderedMenuItems={props.renderedMenuItems}
          getExtracts={props.getExtracts}
          handleSearchId={props.handleSearchId}
          applyFilter={props.applyFilter}
          setApplyFilter={props.setApplyFilter}
          setSearchIds={props.setSearchIds}
          searchIds={props.searchIds}
          selectedSearch={props.selectedSearch}
          setSelectedSearch={props.setSelectedSearch}
          selectedTab={props.selectedTab}
          setSelectedTab={props.setSelectedTab}
          setTypeOfExtract={props.setTypeOfExtract}
          selectedSearches={props.selectedSearches}
          setSelectedSearches={props.setSelectedSearches}
          setTabOpen={props.setTabOpen}
          getDialogState={props.tabOpen}
          closeMainTabDialog={handleCloseMainTab}
          initialLoader={props.initialLoader}
        />
      )}
      <MainPage
        isOpen={openSplit}
        ClientPartner={ClientPartner}
        onClose={handleCloseSplit}
        partnerID={partnerID}
      />
    </>
  );
};

export default ClientDataList;
